import React, { lazy, Suspense, Component, ReactNode } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { blogPosts } from '@/lib/blogRegistry';

// Error boundary to catch dynamic import errors
class BlogErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Blog post loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="max-w-md text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to Load Blog Post</h2>
            <p className="text-gray-600 mb-6">
              We're having trouble loading this blog post. This might be a temporary issue.
            </p>
            <div className="space-y-3">
              <Link 
                to="/blog" 
                className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Back to Blog
              </Link>
              <button 
                onClick={() => window.location.reload()} 
                className="block w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Try Refreshing
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre className="mt-6 text-xs text-left bg-red-50 p-4 rounded overflow-auto">
                {this.state.error.message}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export function BlogPostLoader() {
  const { slug } = useParams<{ slug: string }>();
  
  // Find the post in the blogPosts array by slug
  const post = blogPosts.find(p => p.slug === slug);
  
  if (!slug || !post) {
    console.log('Post not found for slug:', slug);
    return <Navigate to="/blog" replace />;
  }
  
  // Dynamically import the blog post component based on the slug
  const BlogPostComponent = lazy(async () => {
    try {
      // Log the import attempt in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Attempting to import blog post:', slug);
      }
      
      // Use Vite's glob import to ensure all blog posts are included in the build
      // Include subdirectories (unifi/, office365/, etc.)
      const modules = import.meta.glob('./**/*.tsx', { eager: false });
      
      // Try to find the module by checking different possible paths
      let modulePath = `./${slug}.tsx`;
      let moduleImporter = modules[modulePath];
      
      // If not found in main directory, search subdirectories
      if (!moduleImporter) {
        const matchingModule = Object.keys(modules).find(path => 
          path.endsWith(`/${slug}.tsx`)
        );
        if (matchingModule) {
          modulePath = matchingModule;
          moduleImporter = modules[matchingModule];
        }
      }
      
      // Enhanced debugging in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Blog post import:', slug, '->', modulePath);
      }
      
      if (!moduleImporter) {
        console.error('Blog post not found:', slug);
        console.error('Available modules:', Object.keys(modules));
        throw new Error(`Blog post ${slug} not found`);
      }
      
      const module = await moduleImporter() as Record<string, unknown>;
      
      // Log what we got in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Module loaded for', slug, ':', Object.keys(module));
      }
      
      // Handle both default and named exports
      // First check for default export
      if (module.default) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Using default export');
        }
        return module;
      }
      
      // Try to find the component - look for function exports that end with 'Page'
      // Blog components follow the pattern: [ComponentName]Page
      let componentName = Object.keys(module).find(key => 
        typeof module[key] === 'function' && 
        key !== 'default' &&
        key.endsWith('Page')
      );
      
      // Fallback: if no 'Page' suffix found, try any function export
      if (!componentName) {
        componentName = Object.keys(module).find(key => 
          typeof module[key] === 'function' && 
          key !== 'default'
        );
      }
      
      if (componentName) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Found named export:', componentName);
        }
        return { default: module[componentName] };
      } else {
        console.error('No suitable component found in module:', slug, 'Available exports:', Object.keys(module));
        throw new Error(`No React component found in ${slug}. Available exports: ${Object.keys(module).join(', ')}`);
      }
    } catch (error) {
      console.error('Error loading blog post:', slug, error);
      throw error;
    }
  });

  return (
    <BlogErrorBoundary>
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading blog post...</p>
          </div>
        </div>
      }>
        <BlogPostComponent />
      </Suspense>
    </BlogErrorBoundary>
  );
}